import { EventSnapshot } from '../api/types';
import { BASE_URL, getEvent } from '../api/api';
import { mergeSnapshotWithPatch } from '../utils/streamMerge';

export type StreamCallback = (
  upserts: EventSnapshot[],
  deletes: string[]
) => void;

export class CatalogStreamManager {
  private scope: string;
  private stream: EventSource | null = null;
  private subscribers: Set<StreamCallback> = new Set();
  
  // Local cache of snapshots for base_version checking
  private localState: Map<string, EventSnapshot> = new Map();
  
  // Pending queues for throttling updates
  private pendingUpserts: Map<string, EventSnapshot> = new Map();
  private pendingDeletes: Set<string> = new Set();
  private flushIntervalId: number | NodeJS.Timeout | null = null;
  
  // Track events that are currently being resynced to avoid duplicate requests
  private resyncingEvents: Set<string> = new Set();

  constructor(scope: string) {
    this.scope = scope;
  }

  public subscribe(cb: StreamCallback) {
    this.subscribers.add(cb);
    
    if (this.subscribers.size === 1) {
      this.connect();
    }
    
    // Provide initial state immediately to new subscriber
    if (this.localState.size > 0) {
      cb(Array.from(this.localState.values()), []);
    }

    return () => {
      this.subscribers.delete(cb);
      if (this.subscribers.size === 0) {
        this.disconnect();
      }
    };
  }

  private connect() {
    if (this.stream) return;

    const locale = localStorage.getItem('alex-bet-locale') || 'de';
    this.stream = new EventSource(`${BASE_URL}/v1/catalog/stream/${this.scope}?locale=${locale}`);

    this.stream.addEventListener('open', () => {
      console.log(`[CatalogStreamManager][${this.scope}] Connected`);
    });

    this.stream.addEventListener('snapshot', this.handleSnapshotOrCreate);
    this.stream.addEventListener('create', this.handleSnapshotOrCreate);
    this.stream.addEventListener('patch', this.handlePatch);
    this.stream.addEventListener('delete', this.handleDelete);

    this.flushIntervalId = setInterval(() => this.flush(), 1000);
  }

  private disconnect() {
    if (this.stream) {
      this.stream.close();
      this.stream = null;
    }
    if (this.flushIntervalId) {
      clearInterval(this.flushIntervalId as NodeJS.Timeout);
      this.flushIntervalId = null;
    }
    this.localState.clear();
    this.pendingUpserts.clear();
    this.pendingDeletes.clear();
    this.resyncingEvents.clear();
  }

  private handleSnapshotOrCreate = (ev: MessageEvent) => {
    try {
      const msg: EventSnapshot = JSON.parse(ev.data);
      if (!msg.event_id) return;
      
      this.localState.set(msg.event_id, msg);
      this.pendingUpserts.set(msg.event_id, msg);
      this.pendingDeletes.delete(msg.event_id);
      this.resyncingEvents.delete(msg.event_id);
    } catch (err) {
      console.error(`[CatalogStreamManager][${this.scope}] Failed to parse snapshot/create`, err);
    }
  };

  private handlePatch = (ev: MessageEvent) => {
    try {
      const msg = JSON.parse(ev.data);
      const eventId = msg.event_id;
      if (!eventId) return;

      const localEvent = this.localState.get(eventId);
      
      if (!localEvent) {
        // Missing local event: fetch snapshot
        this.resyncEvent(eventId);
        return;
      }

      if (msg.base_version !== undefined && localEvent.version !== msg.base_version) {
        // Version mismatch: fetch snapshot
        console.warn(`[CatalogStreamManager][${this.scope}] Version mismatch for ${eventId} (local: ${localEvent.version}, patch: ${msg.base_version}). Resyncing...`);
        this.resyncEvent(eventId);
        return;
      }

      const updatedEvent = mergeSnapshotWithPatch(localEvent, msg);
      this.localState.set(eventId, updatedEvent);
      this.pendingUpserts.set(eventId, updatedEvent);
    } catch (err) {
      console.error(`[CatalogStreamManager][${this.scope}] Failed to parse patch`, err);
    }
  };

  private handleDelete = (ev: MessageEvent) => {
    try {
      const msg = JSON.parse(ev.data);
      const eventId = msg.event_id || (msg.payload && msg.payload.event_id);
      if (!eventId) return;

      this.localState.delete(eventId);
      this.pendingUpserts.delete(eventId);
      this.pendingDeletes.add(eventId);
    } catch (err) {
      console.error(`[CatalogStreamManager][${this.scope}] Failed to parse delete`, err);
    }
  };

  private async resyncEvent(eventId: string) {
    if (this.resyncingEvents.has(eventId)) return;
    
    this.resyncingEvents.add(eventId);
    try {
      const freshSnapshot = await getEvent(eventId, this.scope);
      if (freshSnapshot && freshSnapshot.event_id) {
        this.localState.set(eventId, freshSnapshot);
        this.pendingUpserts.set(eventId, freshSnapshot);
        this.pendingDeletes.delete(eventId);
      }
    } catch (err) {
      console.error(`[CatalogStreamManager][${this.scope}] Failed to resync event ${eventId}`, err);
    } finally {
      this.resyncingEvents.delete(eventId);
    }
  }

  private flush() {
    if (this.pendingUpserts.size === 0 && this.pendingDeletes.size === 0) return;
    
    const upserts = Array.from(this.pendingUpserts.values());
    const deletes = Array.from(this.pendingDeletes);
    
    this.pendingUpserts.clear();
    this.pendingDeletes.clear();

    this.subscribers.forEach(cb => cb(upserts, deletes));
  }
}

export const liveStreamManager = new CatalogStreamManager('live');
export const preStreamManager = new CatalogStreamManager('pre');
